import { CrewSwitcher } from "@/components/crew-switcher"
import { CurrentBrandDisplay } from "@/components/current-brand-display"
import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"

interface BrandStripProps {
  className?: string
}

/**
 * OKS-385 — BrandStrip (UX Spec §2.1, frame `460:432`).
 *
 * Full-width brand-context row at the top of creator content surfaces (W8
 * missions home first). Shows the active brand and — for a creator in more
 * than one crew — the switch affordance.
 *
 * **Single source of truth (TRD KD-4).** Brand identity + crew list come
 * from the shared `useCurrentContext()` tenancy props, NOT prop-drilled —
 * the same source `CrewSwitcher` already reads. This is the brand-isolation
 * guarantee: the switcher only ever lists the creator's OWN memberships
 * (`availableBrands`), never a foreign crew.
 *
 * **Collapse behaviour:**
 *   - `availableBrands.length > 1` → wraps the SHIPPED `<CrewSwitcher>`
 *     (its locked trigger carries the brand logo + name + chevron and owns
 *     the responsive Popover/Drawer modality — we do not fork it).
 *   - exactly 1 crew → static, non-interactive `<CurrentBrandDisplay>` chip
 *     (no chevron, no switcher).
 *
 * Renders nothing when there is no current brand (e.g. the `no_memberships`
 * empty state) — the page's empty state owns that messaging.
 *
 * # Locked testid
 *   - `brand-strip` (container; present in both switchable + static modes)
 */
export function BrandStrip({ className }: BrandStripProps) {
  const { currentBrand, availableBrands } = useCurrentContext()

  if (!currentBrand) return null

  const switchable = availableBrands.length > 1

  return (
    <div
      data-testid="brand-strip"
      data-switchable={switchable}
      className={cn(
        "border-border/60 flex w-full items-center border-b pb-3",
        className,
      )}
    >
      {switchable ? (
        <CrewSwitcher className="w-full justify-between" />
      ) : (
        <CurrentBrandDisplay />
      )}
    </div>
  )
}
